import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bot, X, Send, Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";

export default function AIWidget() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([{ from: "bot", text: "Hey! I'm Nova, 9nocAI's AI assistant. How can I help you grow with AI today?" }]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const sessionId = useRef(`web-${Math.random().toString(36).slice(2)}-${Date.now()}`);
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, open, typing]);

  const send = async () => {
    const text = input.trim();
    if (!text || typing) return;
    const history = msgs.map((m) => ({ role: m.from === "user" ? "user" : "assistant", text: m.text }));
    setMsgs((m) => [...m, { from: "user", text }]);
    setInput("");
    setTyping(true);
    try {
      const { data } = await api.post("/chat", { session_id: sessionId.current, message: text, history });
      setMsgs((m) => [...m, { from: "bot", text: data.reply }]);
    } catch (e) {
      setMsgs((m) => [...m, { from: "bot", text: "Sorry, I'm having a moment! Please reach us on WhatsApp at +91 7042141004 and we'll help right away." }]);
    } finally {
      setTyping(false);
    }
  };

  return (
    <>
      <motion.button
        onClick={() => setOpen((v) => !v)}
        data-testid="ai-widget-toggle"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 1.7, type: "spring", stiffness: 260, damping: 18 }}
        whileHover={{ scale: 1.08 }}
        className="fixed bottom-6 right-6 z-50 grid h-14 w-14 place-items-center rounded-full bg-gradient-to-br from-[#00E5FF] to-[#7B61FF] glow-cyan"
      >
        {open ? <X className="h-6 w-6 text-[#0B1220]" /> : <Bot className="h-7 w-7 text-[#0B1220]" />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-24 right-6 z-50 w-[calc(100vw-3rem)] sm:w-80 rounded-3xl glass-strong overflow-hidden"
            data-testid="ai-widget-panel"
          >
            <div className="flex items-center gap-3 border-b border-white/10 p-4">
              <span className="grid h-9 w-9 place-items-center rounded-full bg-gradient-to-br from-[#00E5FF] to-[#7B61FF]"><Sparkles className="h-4 w-4 text-[#0B1220]" /></span>
              <div><div className="text-sm font-semibold">Nova · 9nocAI</div><div className="text-xs text-[#00FFB2]">● AI Assistant Online</div></div>
            </div>
            <div className="h-64 overflow-y-auto p-4 space-y-3">
              {msgs.map((m, i) => (
                <div key={i} className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm ${m.from === "user" ? "bg-[#00E5FF] text-[#0B1220]" : "bg-white/5 text-white/85"}`}>{m.text}</div>
                </div>
              ))}
              {typing && (
                <div className="flex justify-start" data-testid="ai-widget-typing">
                  <div className="rounded-2xl bg-white/5 px-3.5 py-3 flex gap-1">
                    {[0, 1, 2].map((d) => (
                      <motion.span key={d} className="h-1.5 w-1.5 rounded-full bg-[#00E5FF]"
                        animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1, repeat: Infinity, delay: d * 0.2 }} />
                    ))}
                  </div>
                </div>
              )}
              <div ref={endRef} />
            </div>
            <div className="flex gap-2 p-3 border-t border-white/10">
              <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Ask Nova anything..." data-testid="ai-widget-input" className="bg-white/5 border-white/10 focus-visible:ring-[#00E5FF] h-10" />
              <button onClick={send} disabled={typing} data-testid="ai-widget-send" className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#00E5FF] text-[#0B1220] disabled:opacity-50"><Send className="h-4 w-4" /></button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
